// Some helper functions for sorting course instances and getting open seats/waitlist lengths from the post meta data

// sort the courses for the upcoming course dates ribbon
export const sortCourses = (courses, userCategory, loggedIn, hasOnlineOption = false) => {
    // if the user is not logged in don't sort anything
    if(!loggedIn){
        return courses;
    }

    let coursesCopy = [...courses];
    let retCourses = [];
    let regIsOpen = false;
    // get courses with open seats ordered by date (data comes in sorted by date)
    coursesCopy.map((course, index) => {
        regIsOpen = isRegistrationOpenByCourse(course);
        // add an index key to the course object for the anchor tags to jump to the right course in the accordions
        course = {...course, Timber_Post: {...course.Timber_Post, anchor_index: index}};
        
        // if the courses registration is closed then dont add it to the list
        if(!regIsOpen || regIsOpen == "false"){
            return;
        }
        // if the user is a clinical instructor combine the clinical instructor and mentor open seats for sorting
        if(userCategory === 'Clinical Instructor' && (course.Timber_Post.clinical_instructor_open_seats + course.Timber_Post.mentor_open_seats) > 0){
            retCourses.push(course);
        } else if(course.Timber_Post[getOpenSeatSortCategoryKey(userCategory)] > 0){
            retCourses.push(course);
        }
    });

    // if we dont have 3 courses with open seats then we need to apend courses with the shortest waitlists
    if(retCourses.length < 3){
        coursesCopy.sort((a, b) => {
            if(userCategory === 'Clinical Instructor'){
                // if the user is a clinical instructor, we need the waitlist sort category key to be the the shorter of the clinical instructor and mentor waitlists
                return Math.min(a.Timber_Post[getWaitlistSortCategoryKey(userCategory)], a.Timber_Post[getWaitlistSortCategoryKey('Mentor')]) - Math.min(b.Timber_Post[getWaitlistSortCategoryKey(userCategory)], b.Timber_Post[getWaitlistSortCategoryKey('Mentor')]);
            } else {
                return a.Timber_Post[getWaitlistSortCategoryKey(userCategory)] - b.Timber_Post[getWaitlistSortCategoryKey(userCategory)];
            }
        });
        // courses are sorted by the shortest waitlist, add them to the return courses until return courses has 3 courses
        coursesCopy.map((course) => {
            regIsOpen = isRegistrationOpenByCourse(course);
            if(retCourses.length < 3 && regIsOpen){
                // if the user is a clinical instructor combine the clinical instructor and mentor open seats for sorting
                if(userCategory === 'Clinical Instructor'){
                    if((course.Timber_Post.clinical_instructor_open_seats + course.Timber_Post.mentor_open_seats) <= 0){
                        retCourses.push(course);
                    }
                } else if(course.Timber_Post[getOpenSeatSortCategoryKey(userCategory)] <= 0){
                    retCourses.push(course);
                }
            }
        });
    }

    return retCourses;
}

// get the open seats key for the users category
export const getOpenSeatSortCategoryKey = (userCategory) => {
    switch(userCategory){
        case 'Participant':
            return 'participant_open_seats';
        case 'Mentor':
            return 'mentor_open_seats';
        case 'Clinical Instructor':
            return 'clinical_instructor_open_seats';
        case 'Alumni Audit':
            return 'alumni_audit_open_seats';
    }
}

// get the waitlist length key for the users category
export const getWaitlistSortCategoryKey = (userCategory) => {
    switch(userCategory){
        case 'Participant':
            return 'participant_waitlist_length';
        case 'Mentor':
            return 'mentor_waitlist_length';
        case 'Clinical Instructor':
            return 'clinical_instructor_waitlist_length';
        case 'Alumni Audit':
            return 'alumni_audit_waitlist_length';
    }
}

// get the total number of open seats of all categories for a course
export const getTotalOpenSeats = (course) => {
    let totalOpenSeats = 0;
    Object.entries(course.Timber_Post).map(([key, value]) => {
        if(key.includes('open_seats')){
            // convert value to integer and add to total
            totalOpenSeats += parseInt(value);
        }
    });
    return totalOpenSeats;
}

// get the number of open seats for a given userCategory
export const getOpenSeats = (course, userCategory) => {
    if(course.Timber_Post[getOpenSeatSortCategoryKey(userCategory)] === ""){
         return 0;
    } else if(course.Timber_Post[getOpenSeatSortCategoryKey(userCategory)] < 0){
        return 0;
    } else {
        return parseInt(course.Timber_Post[getOpenSeatSortCategoryKey(userCategory)])
    }
}

// for each courses, check the users category waitlist, and return the course id of the shortest waitlist
export const getShortestWaitlistCourseId = (courses, userCategory) => {
    let shortestWaitlist = 999;
    let shortestWaitlistCourseId = null;

    // if the user category is "Clinical Instructor" then we have a special case
    if (userCategory === 'Clinical Instructor') {

        courses.map((course) => {
            // get the most up to date registration open status or else get the cached status in the post meta
            if(!isRegistrationOpenByCourse(course)){
                return;
            }

            const mentorOpenSeats = getOpenSeats(course, 'Mentor');
            const clinicalInstructorOpenSeats = getOpenSeats(course, 'Clinical Instructor');

            // check if both mentor and clinical instructor categories have open seats
            if (mentorOpenSeats > 0 || clinicalInstructorOpenSeats > 0) {
                return; // skip this course
            }

            const mentorWaitlistLength = parseInt(course.Timber_Post[getWaitlistSortCategoryKey('Mentor')]);
            const clinicalInstructorWaitlistLength = parseInt(course.Timber_Post[getWaitlistSortCategoryKey('Clinical Instructor')]);

            // update the shortest waitlist if necessary
            if (mentorWaitlistLength < shortestWaitlist || clinicalInstructorWaitlistLength < shortestWaitlist) {
                shortestWaitlist = Math.min(mentorWaitlistLength, clinicalInstructorWaitlistLength);
                shortestWaitlistCourseId = course.Timber_Post.ID;
            }
        });

    } else {
        // for other user categories, follow the existing logic
        courses.map((course) => {
            // get the most up to date registration open status or else get the cached status in the post meta
            if(!isRegistrationOpenByCourse(course)){
                return;
            }

            if (parseInt(course.Timber_Post[getWaitlistSortCategoryKey(userCategory)]) < shortestWaitlist && getOpenSeats(course, userCategory) <= 0) {
                shortestWaitlist = parseInt(course.Timber_Post[getWaitlistSortCategoryKey(userCategory)]);
                shortestWaitlistCourseId = course.Timber_Post.ID;
            }
        });
    }

    return shortestWaitlistCourseId;
}

// get the number of open seats for a given userCategory from an instance details response
export const getOpenSeatsFromInstanceDetails = (instanceDetails, userCategory) => {
    // loop through catDetails and if catName is the same as userCategory, return the open seats
    let openSeats = 0;

    if(instanceDetails.catDetails){
        Object.keys(instanceDetails.catDetails).forEach((catIndex) => {
            const catDetail = instanceDetails.catDetails[catIndex];
            if (catDetail.catName === userCategory) {
                if(catDetail.seatsData){
                    if(catDetail.seatsData.numOfSeats){
                        openSeats += catDetail.seatsData.numOfSeats;
                    }
                }
            }
        });
    } else {
        return null;
    }

    return openSeats;
}

// get the waitlist length for a given userCategory from an instance details response
export const getWaitlistLengthFromInstanceDetails = (instanceDetails, userCategory) => {
    // loop through catDetails and if catName is the same as userCategory, return the waitlist length
    let waitlistLength = 0;

    if(instanceDetails.catDetails){
        Object.keys(instanceDetails.catDetails).forEach((catIndex) => {
            const catDetail = instanceDetails.catDetails[catIndex];
            if(catDetail.seatsData){
                if (catDetail.catName === userCategory) {
                    waitlistLength += catDetail.seatsData.waitlisted;
                }
            }
        });
    } else {
        return null;
    }
    return waitlistLength;
}

// check if registration is open by course instance
export const isRegistrationOpen = (instanceDetails) => {
    let threeDays = 259200;
    let startDate = new Date(instanceDetails.registrationOpensDate * 1000);
    let endDate = new Date((instanceDetails.registrationClosesDate ? instanceDetails.registrationClosesDate : (instanceDetails.startDate - threeDays)) * 1000);
    let currentDate = new Date();

    if(currentDate > startDate && currentDate < endDate){
        return true;
    } else {
        return false;
    }
}

// check if reg is open by timestamps
export const isRegistrationOpenByTimestamps = (registrationOpensDate, registrationClosesDate) => {
    let threeDays = 259200;
    let startDate = new Date(registrationOpensDate * 1000);
    let endDate = new Date((registrationClosesDate ? registrationClosesDate : (startDate - threeDays)) * 1000);
    let currentDate = new Date();

    if(currentDate > startDate && currentDate < endDate){
        return true;
    } else {
        return false;
    }
}

const isRegistrationOpenByCourse = (course) => {
    let regIsOpen = false;
     // check if we have up to date registration open status or else use the post meta cached data 
        if(course.instanceDetails && course.instanceDetails.course_details){
            regIsOpen = isRegistrationOpen(course.instanceDetails.course_details);
        }
        else {
            regIsOpen = course.Timber_Post.registration_open;
        }
    return regIsOpen;
}
export const is165Course = (courseNumber) => {
    let is165 = false;
    if (courseNumber === '165') {
        is165  = true;
    }
    return is165;
}

export const getCurrentOnlineCourse = (courses) => {
    let currentOnlineCourse = null;
    // if we dont have current course data then we don't have an online course so we'll return null
    //if(courses.current == undefined ||courses.current.current_course_data == undefined || courses.current.current_course_data.length == 0){
    if(courses?.current?.current_course_data){
        return currentOnlineCourse;
    }

    if(Array.isArray(courses)){
        courses.map((course, index) => {
            if(index === 0 && course.isOnline){
                currentOnlineCourse = course;
            }
        });
    }
    return currentOnlineCourse;
}

// get the open seats key for the users category
export const getCategories = () => {

    return [
        'Participant',
        'Mentor',
        'Clinical Instructor',
        'Alumni Audit',
    ]
}

export const getEligibleCategory = (futureInstances, userHighestCategory) => {
    // flag to check if the users category is eligible for any of the future instances
    let categoryIsAllowed = false;

    if(Array.isArray(futureInstances)){
        // loop through the future instances and check if the user is eligible for any of them
        futureInstances.map((instance) => {
            // if the user is eligible for this course, update the userHighestCategory
            if(categoryIsEligible(instance, userHighestCategory)){
                categoryIsAllowed = true;
            }
        });
    }

    if(categoryIsAllowed){
        return userHighestCategory;
    } else {
        return "Participant";
    }
}

const categoryIsEligible = (course, userHighestCategory) => {
    if(course.Timber_Post.allowed_categories === undefined){
        return false;
    }

    let allowed_categories = JSON.parse(course.Timber_Post.allowed_categories);

    if(allowed_categories.includes(userHighestCategory) && isRegistrationOpenByCourse(course)){
        return true;
    } else {
        return false;
    }
}

export const completedPastOnlineCourses = (courses) => {
    let hasPastOnlineCourses = false;
    if(Array.isArray(courses?.pastCourses)){
        courses?.pastCourses.map((course) => {
            if(course.digitalCourse){
                hasPastOnlineCourses = true;
            }
        });
    }
    return hasPastOnlineCourses;
}

export const getOnlineCourseCompletedText = (courses) => {
    let onlineCourseText = 'Completed';
    if(Array.isArray(courses?.pastCourses)){
        courses?.pastCourses.map((course) => {
            if(course.digitalCourse){
                onlineCourseText = course.dateAttended;
            }
        });
    }
    return onlineCourseText;
}